import React, { useEffect, useState, useMemo } from 'react';
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
  Chip,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { searchMembersWithSubMembers } from '../../../controllers/memberManagementController';
import { createMeetingAttendance } from '../../../controllers/meetingsController';
import { resolveGroupsAssetUrl } from '../../../api/httpClientGroups';
import { toast } from 'react-toastify';

const AddMeetingAttendeeDialog = ({ open, onClose, meetingId, allowGuests, onAdded }) => {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [guestsList, setGuestsList] = useState([]);
  const [guestForm, setGuestForm] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
  });

  useEffect(() => {
    if (!open) return;
    if (!query || query.length < 2) {
      setOptions([]);
      return;
    }
    let active = true;
    setLoading(true);
    searchMembersWithSubMembers(query)
      .then((data) => { if (active) setOptions(data || []); })
      .catch(() => { if (active) setOptions([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open, query]);

  const canAddGuestToList = useMemo(() => {
    if (!allowGuests || !selected) return false;
    return Boolean(guestForm.firstName.trim() && guestForm.lastName.trim());
  }, [allowGuests, selected, guestForm]);

  const canAddAttendee = useMemo(() => {
    return Boolean(meetingId && (selected?.id || selected?.Id));
  }, [meetingId, selected]);

  const handleAddGuestToList = () => {
    if (!canAddGuestToList) return;
    setGuestsList((prev) => [
      ...prev,
      {
        firstName: guestForm.firstName.trim(),
        lastName: guestForm.lastName.trim(),
        phoneNumber: guestForm.phoneNumber.trim() || null,
      },
    ]);
    setGuestForm({ firstName: '', lastName: '', phoneNumber: '' });
  };

  const handleRemoveGuestFromList = (index) => {
    setGuestsList((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAdd = async () => {
    if (!canAddAttendee) return;
    setErrorText('');
    setSubmitting(true);
    try {
      const personId = selected?.id || selected?.Id;
      const type = typeof selected?.type === 'number' ? selected.type : (typeof selected?.Type === 'number' ? selected.Type : 0);
      await createMeetingAttendance({
        meetingId,
        personId,
        type,
        isExempt: false,
        guests: guestsList.length > 0 ? guestsList.map((g) => ({
          firstName: g.firstName,
          lastName: g.lastName,
          phoneNumber: g.phoneNumber || undefined,
        })) : undefined,
      });
      setSelected(null);
      setQuery('');
      setOptions([]);
      setGuestsList([]);
      setGuestForm({ firstName: '', lastName: '', phoneNumber: '' });
      toast.success('Katılımcı başarıyla eklendi');
      await onAdded?.();
      onClose?.();
    } catch (e) {
      const errorMessage = e?.message || 'Katılımcı eklenemedi';
      setErrorText(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!open) {
      setSelected(null);
      setQuery('');
      setOptions([]);
      setGuestsList([]);
      setGuestForm({ firstName: '', lastName: '', phoneNumber: '' });
      setErrorText('');
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Katılımcı Ekle</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Autocomplete
            sx={{ minWidth: 280 }}
            options={options}
            filterOptions={(x) => x}
            isOptionEqualToValue={(o, v) => (o?.id || o?.Id) === (v?.id || v?.Id)}
            getOptionLabel={(o) => o.fullName || o.FullName || ''}
            value={selected}
            onChange={(e, v) => {
              setSelected(v);
              setGuestsList([]);
              setGuestForm({ firstName: '', lastName: '', phoneNumber: '' });
            }}
            onInputChange={(e, v) => setQuery(v)}
            loading={loading}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.id || option.Id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar src={resolveGroupsAssetUrl(option.profilePictureUrl || option.ProfilePictureUrl)} sx={{ width: 28, height: 28 }} />
                <Typography variant="body2">{option.fullName || option.FullName}</Typography>
                <Chip label={((typeof option.type === 'number' ? option.type : option.Type) === 0) ? 'Üye' : 'Yakın'} size="small" sx={{ height: 20 }} />
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Üye / Yakın ara"
                placeholder="İsim yazın..."
                error={Boolean(errorText)}
                helperText={errorText}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress color="inherit" size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          {selected && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Avatar src={resolveGroupsAssetUrl(selected.profilePictureUrl || selected.ProfilePictureUrl)} sx={{ width: 28, height: 28 }} />
              <Typography variant="body2" fontWeight={600}>{selected.fullName || selected.FullName}</Typography>
              <Chip label={((typeof selected.type === 'number' ? selected.type : selected.Type) === 0) ? 'Üye' : 'Yakın'} size="small" />
            </Stack>
          )}
          <Button variant="contained" onClick={handleAdd} disabled={!canAddAttendee || submitting} size="small" sx={{ alignSelf: 'flex-start' }}>
            {submitting ? 'Ekleniyor...' : 'Katılımcı Ekle'}
          </Button>

          {selected && allowGuests && (
            <>
              <Divider />
              <Typography variant="subtitle2" fontWeight={600}>Misafir Ekle (isteğe bağlı)</Typography>
              {guestsList.length > 0 && (
                <Stack spacing={1}>
                  {guestsList.map((guest, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 1.5,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Typography variant="body2">{guest.firstName} {guest.lastName}{guest.phoneNumber ? ` - ${guest.phoneNumber}` : ''}</Typography>
                      <IconButton size="small" color="error" onClick={() => handleRemoveGuestFromList(index)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Stack>
              )}
              <Stack direction="row" spacing={1}>
                <TextField
                  label="Ad"
                  size="small"
                  value={guestForm.firstName}
                  onChange={(e) => setGuestForm((f) => ({ ...f, firstName: e.target.value }))}
                  inputProps={{ maxLength: 100 }}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Soyad"
                  size="small"
                  value={guestForm.lastName}
                  onChange={(e) => setGuestForm((f) => ({ ...f, lastName: e.target.value }))}
                  inputProps={{ maxLength: 100 }}
                  sx={{ flex: 1 }}
                />
              </Stack>
              <TextField
                label="Telefon (opsiyonel)"
                size="small"
                fullWidth
                value={guestForm.phoneNumber}
                onChange={(e) => setGuestForm((f) => ({ ...f, phoneNumber: e.target.value }))}
                inputProps={{ maxLength: 20 }}
              />
              <Button variant="outlined" size="small" onClick={handleAddGuestToList} disabled={!canAddGuestToList}>
                Listeye Ekle
              </Button>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Kapat</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddMeetingAttendeeDialog;
